import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import {
  CreateSparkCampaignResponseDto,
  CreateSparkCampaignDto,
  TiktokAdGroupLocationOption,
} from '@/app/common/general-types/tiktok-spark-ads.dto';
import {
  SparkAdAccountDto,
  SparkCreatorVideoDto,
  SparkForecastDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { TiktokSparkAdsService } from '@/app/services/tiktok-spark-ads/tiktok-spark-ads.service';
import { CreateSparkCampaign } from '../../../boost-tiktok-v2/service/create-spark-campaign/create-spark-campaign';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { SparkAuthService } from '../../services/spark-auth.service';
import { SparkAdsReportingService } from '../../services/spark-ads-reporting.service';
import { SparkAdsStore } from '../../services/spark-ads-store';
import { SparkGlossaryService } from '../../services/spark-glossary.service';
import { SparkBuilderStep, SparkLoadState, SparkPostSort } from '../../spark-ads.types';
import { formatCurrency } from '../../spark-ads.format';
import {
  DEFAULT_SPARK_OBJECTIVE,
  MIN_DAILY_BUDGET,
  MIN_LIFETIME_BUDGET,
  BUDGET_SLIDER_MAX,
  BUDGET_SLIDER_STEP,
  SPARK_AGE_GROUPS,
  SPARK_CALLS_TO_ACTION,
  SPARK_GENDERS,
  SPARK_OBJECTIVES,
  STEP_DEFINITIONS,
  SparkAgeGroup,
  SparkGender,
} from './spark-ads-builder.config';
import {
  blockedReasonForStep,
  normaliseUrl,
  readyNoteForStep,
} from './spark-ads-builder.validation';
import { addableMarkets, toTikTokTime } from './spark-ads-builder.helpers';
import { EstimateBanner } from './atoms/estimate-banner/estimate-banner';
import { PhonePreview } from './atoms/phone-preview/phone-preview';

/** What the launch sequence is currently doing, for the progress copy. */
type LaunchStage = 'idle' | 'campaign' | 'adGroup' | 'ad' | 'done' | 'error';

/**
 * Guided Campaign → Ad group → Spark ad wizard, then review and launch.
 *
 * Wizard state lives in the shared `CreateSparkCampaign` service, which already models
 * TikTok's full object graph and keeps optimisation goal, billing event and bid type
 * consistent with the chosen objective.
 */
@Component({
  selector: 'sa-spark-ads-builder',
  imports: [EstimateBanner, PhonePreview],
  templateUrl: './spark-ads-builder.html',
  styleUrl: './spark-ads-builder.scss',
  host: { class: 'block w-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparkAdsBuilder {
  readonly briefId = input.required<string>();

  protected readonly wizard = inject(CreateSparkCampaign);
  protected readonly glossaryService = inject(SparkGlossaryService);
  protected readonly store = inject(SparkAdsStore);
  private readonly sparkAdsService = inject(TiktokSparkAdsService);
  private readonly authService = inject(SparkAuthService);
  private readonly reportingService = inject(SparkAdsReportingService);
  private readonly manageBriefTabsService = inject(ManageBriefTabsService);
  private readonly toast = inject(ToastNotificationService);
  private readonly destroyRef = inject(DestroyRef);

  // --- Static option lists --------------------------------------------------
  protected readonly steps = STEP_DEFINITIONS;
  protected readonly objectives = SPARK_OBJECTIVES;
  protected readonly ageGroups = SPARK_AGE_GROUPS;
  protected readonly genders = SPARK_GENDERS;
  protected readonly callsToAction = SPARK_CALLS_TO_ACTION;
  protected readonly budgetSliderMax = BUDGET_SLIDER_MAX;
  protected readonly budgetSliderStep = BUDGET_SLIDER_STEP;

  // --- Wizard position ------------------------------------------------------
  protected readonly step = signal<SparkBuilderStep>(1);
  protected readonly advancedOpen = signal(false);
  /** Take A is the library grid; Take B pastes a TikTok auth code. */
  protected readonly usePastedCode = signal(false);
  protected readonly pastedCode = signal('');
  protected readonly postSort = signal<SparkPostSort>('topPerforming');

  // --- Loaded data ----------------------------------------------------------
  protected readonly libraryState = signal<SparkLoadState>('idle');
  protected readonly library = signal<SparkCreatorVideoDto[]>([]);
  protected readonly requestingVideoId = signal<string | undefined>(undefined);
  protected readonly forecast = signal<SparkForecastDto | undefined>(undefined);
  protected readonly existingCampaign = signal<CreateSparkCampaignResponseDto | undefined>(
    undefined,
  );
  /** Which TikTok ad account will spend, and whether it is SHOUT's rather than theirs. */
  protected readonly adAccount = signal<SparkAdAccountDto | undefined>(undefined);

  // --- Launch ---------------------------------------------------------------
  protected readonly launchStage = signal<LaunchStage>('idle');
  protected readonly launchError = signal<string | null>(null);
  protected readonly launchedAdId = signal<string | undefined>(undefined);

  private readonly loadLibrary$ = new Subject<void>();
  private readonly loadForecast$ = new Subject<void>();

  // --- Derived --------------------------------------------------------------

  protected readonly budgetIsDaily = computed(
    () => this.wizard.selectedBudgetModeAdGroup() === 'BUDGET_MODE_DAY',
  );

  protected readonly minBudget = computed(() =>
    this.budgetIsDaily() ? MIN_DAILY_BUDGET : MIN_LIFETIME_BUDGET,
  );

  protected readonly selectedVideo = computed<SparkCreatorVideoDto | undefined>(() => {
    const videoId = this.wizard.video_id();
    return this.library().find((entry) => entry.videoId === videoId);
  });

  /** Only authorised, moderation-cleared posts can carry budget. */
  protected readonly selectablePosts = computed(() => {
    const sorted = [...this.library()].sort((a, b) =>
      this.postSort() === 'topPerforming'
        ? (b.video.engagement_rate ?? 0) - (a.video.engagement_rate ?? 0)
        : postedAt(b) - postedAt(a),
    );
    return sorted.filter((entry) => entry.moderation.status !== 'declined');
  });

  protected readonly blockedReason = computed(() =>
    blockedReasonForStep(this.step(), {
      campaignName: this.wizard.campaignName(),
      objectiveApiValue: this.wizard.selectedAdvertisingObjective()?.api_value,
      advertiserId: this.wizard.selectedAdvertiserAccountId(),
      adGroupName: this.wizard.adGroupName(),
      locationIds: this.wizard.location_ids(),
      budget: this.wizard.budgetAdGroup(),
      budgetIsDaily: this.budgetIsDaily(),
      startDate: this.wizard.startDateAdGroup(),
      endDate: this.wizard.endDateAdGroup(),
      selectedVideoId: this.wizard.video_id(),
      selectedVideoAuthorised: this.selectedVideo()?.auth.state === 'authorised',
      callToAction: this.wizard.call_to_action(),
      landingPageUrl: this.wizard.landing_page_url(),
    }),
  );

  protected readonly canContinue = computed(() => this.blockedReason() === null);

  protected readonly readyNote = computed(() => readyNoteForStep(this.step()));

  protected readonly continueLabel = computed(() =>
    this.step() === 3 ? 'Review & launch' : 'Continue',
  );

  /** Hidden on step 3 so the post grid and phone preview have room. */
  protected readonly showSummary = computed(() => this.step() < 3);

  protected readonly objectiveLabel = computed(
    () =>
      this.objectives.find(
        (option) => option.apiValue === this.wizard.selectedAdvertisingObjective()?.api_value,
      )?.label ?? 'No goal chosen',
  );

  protected readonly callToActionLabel = computed(
    () =>
      this.callsToAction.find((option) => option.value === this.wizard.call_to_action())?.label ??
      'Learn more',
  );

  protected readonly isLaunching = computed(() =>
    ['campaign', 'adGroup', 'ad'].includes(this.launchStage()),
  );

  protected readonly hasLaunched = computed(() => this.launchStage() === 'done');

  /**
   * A frequency cap is mandatory for REACH ad groups and rejected for other goals.
   *
   * The wizard service derives the optimisation goal from the campaign objective, so
   * this follows the goal it settles on rather than the objective itself.
   */
  private readonly needsFrequencyCap = computed(
    () => this.wizard.optimization_goal() === 'REACH',
  );

  protected readonly launchProgressLabel = computed(() => {
    switch (this.launchStage()) {
      case 'campaign':
        return 'Creating the campaign…';
      case 'adGroup':
        return 'Setting up the audience and budget…';
      case 'ad':
        return 'Submitting the Spark ad to TikTok…';
      default:
        return '';
    }
  });

  constructor() {
    this.seedDefaults();

    this.loadLibrary$
      .pipe(
        switchMap(() => {
          this.libraryState.set('loading');
          return this.reportingService.getCreatorVideos(this.briefId()).pipe(
            catchError(() => {
              this.libraryState.set('error');
              return of(undefined);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response) {
          this.library.set(response.videos);
          this.libraryState.set('loaded');
          this.preselectCarriedVideo();
        }
      });

    this.loadForecast$
      .pipe(
        switchMap(() => {
          const advertiserId = this.wizard.selectedAdvertiserAccountId();
          if (!advertiserId) {
            return of(undefined);
          }
          return this.reportingService
            .getForecast({
              briefId: this.briefId(),
              advertiserId,
              objectiveType: this.wizard.selectedAdvertisingObjective()?.api_value,
              locationIds: [...this.wizard.location_ids()],
              age: [...this.wizard.age()],
              gender: this.wizard.gender(),
              languages: [...this.wizard.languages()],
              placements: [...this.wizard.placements()],
            })
            .pipe(catchError(() => of(undefined)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((forecast) => this.forecast.set(forecast));

    queueMicrotask(() => {
      this.loadAdAccount();
      this.loadExistingCampaign();
      this.loadLibrary$.next();
    });

    /** Re-estimate whenever the targeting that feeds it changes. */
    effect(() => {
      this.wizard.location_ids();
      this.wizard.age();
      this.wizard.gender();
      this.wizard.selectedAdvertiserAccountId();
      this.loadForecast$.next();
    });
  }

  // --- Navigation -----------------------------------------------------------

  protected goToStep(step: SparkBuilderStep): void {
    this.step.set(step);
  }

  protected next(): void {
    if (!this.canContinue()) {
      return;
    }
    this.step.update((current) => Math.min(4, current + 1) as SparkBuilderStep);
  }

  protected back(): void {
    this.step.update((current) => Math.max(1, current - 1) as SparkBuilderStep);
  }

  protected stepStatus(step: number): 'done' | 'active' | 'upcoming' {
    if (step < this.step()) {
      return 'done';
    }
    return step === this.step() ? 'active' : 'upcoming';
  }

  protected stepRowClass(step: number): string {
    return this.stepStatus(step) === 'active'
      ? 'border-brand-studio/35 bg-card shadow-md'
      : 'border-transparent bg-transparent hover:bg-muted/50';
  }

  protected stepDotClass(step: number): string {
    switch (this.stepStatus(step)) {
      case 'done':
        return 'bg-success text-white';
      case 'active':
        return 'bg-brand-studio text-white';
      default:
        return 'bg-muted text-secondary';
    }
  }

  // --- Step 1: campaign -----------------------------------------------------

  protected setCampaignName(value: string): void {
    this.wizard.campaignName.set(value);
  }

  protected selectObjective(apiValue: string, label: string): void {
    this.wizard.selectedAdvertisingObjective.set({ objective: label, api_value: apiValue });
    // Keeps optimisation goal, billing event and bid type valid for the new objective.
    this.wizard.applyOptimizationGoal(
      this.wizard.optimization_goal(),
      apiValue,
    );
    // TikTok returns different valid markets per objective, so re-fetch them.
    this.loadTargetingOptions();
  }

  protected isObjectiveSelected(apiValue: string): boolean {
    return this.wizard.selectedAdvertisingObjective()?.api_value === apiValue;
  }

  protected objectiveCardClass(apiValue: string): string {
    return this.isObjectiveSelected(apiValue)
      ? 'border-2 border-brand-studio bg-brand-studio/5 shadow-md'
      : 'border border-border bg-card hover:border-brand-studio/40';
  }

  protected toggleBudgetOptimise(): void {
    this.wizard.budgetOptimizeOn.update((on) => !on);
  }

  // --- Step 2: ad group -----------------------------------------------------

  protected setAdGroupName(value: string): void {
    this.wizard.adGroupName.set(value);
  }

  protected toggleAge(age: SparkAgeGroup): void {
    this.wizard.age.update((selected) =>
      selected.includes(age) ? selected.filter((item) => item !== age) : [...selected, age],
    );
  }

  protected isAgeSelected(age: SparkAgeGroup): boolean {
    return this.wizard.age().includes(age);
  }

  protected setGender(gender: SparkGender): void {
    this.wizard.gender.set(gender);
  }

  protected removeLocation(locationId: string): void {
    this.wizard.location_ids.update((ids) => ids.filter((id) => id !== locationId));
  }

  protected locationLabel(locationId: string): string {
    return this.wizard.locationLabelsById().get(locationId) ?? locationId;
  }

  /** Markets TikTok accepts for this advertiser and objective, minus those already chosen. */
  protected readonly addableMarkets = computed(() =>
    addableMarkets(this.wizard.adGroupLocations(), this.wizard.location_ids()),
  );

  /** True once TikTok has answered but offered nothing — distinct from still loading. */
  protected readonly hasNoMarketOptions = computed(
    () => (this.wizard.adGroupLocations() ?? []).length === 0,
  );

  protected readonly marketsLoading = computed(
    () => this.wizard.adGroupLocations() === undefined,
  );

  protected addLocation(locationId: string): void {
    if (!locationId) {
      return;
    }
    this.wizard.location_ids.update((ids) =>
      ids.includes(locationId) ? ids : [...ids, locationId],
    );
  }

  /** `<select>` is a one-shot picker here: adding resets it back to the prompt. */
  protected onMarketSelected(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.addLocation(select.value);
    select.value = '';
  }

  protected setBudgetMode(daily: boolean): void {
    this.wizard.selectedBudgetModeAdGroup.set(daily ? 'BUDGET_MODE_DAY' : 'BUDGET_MODE_TOTAL');
  }

  protected setBudget(value: string): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      this.wizard.budgetAdGroup.set(parsed);
    }
  }

  protected setStartDate(value: string): void {
    this.wizard.startDateAdGroup.set(value);
  }

  protected setEndDate(value: string): void {
    this.wizard.endDateAdGroup.set(value);
  }

  protected toggleAdvanced(): void {
    this.advancedOpen.update((open) => !open);
  }

  protected segmentClass(selected: boolean): string {
    return selected
      ? 'border-brand-studio bg-brand-studio text-white'
      : 'border-border bg-card text-secondary hover:border-brand-studio/40';
  }

  // --- Step 3: Spark ad -----------------------------------------------------

  protected selectPost(entry: SparkCreatorVideoDto): void {
    this.wizard.video_id.set(entry.videoId);
    this.wizard.selectedCreative.set(entry.video);
    this.usePastedCode.set(false);
  }

  protected isPostSelected(entry: SparkCreatorVideoDto): boolean {
    return this.wizard.video_id() === entry.videoId && !this.usePastedCode();
  }

  protected postCardClass(entry: SparkCreatorVideoDto): string {
    return this.isPostSelected(entry)
      ? 'border-2 border-brand-studio shadow-lg -translate-y-0.5'
      : 'border border-border shadow-sm hover:border-brand-studio/40';
  }

  /** Only an authorised post can be chosen; the card says why when it cannot. */
  protected postIsSelectable(entry: SparkCreatorVideoDto): boolean {
    return entry.auth.state === 'authorised';
  }

  protected postBlockedNote(entry: SparkCreatorVideoDto): string {
    switch (entry.auth.state) {
      case 'requested':
        return 'Waiting on the creator';
      case 'rejected':
        return 'Creator declined';
      case 'expired':
        return 'Permission expired';
      case 'unknown':
        return 'Permission unknown';
      default:
        return 'Needs the creator’s permission';
    }
  }

  /** A post can be asked for, or checked again, until the creator has accepted. */
  protected canRequestPost(entry: SparkCreatorVideoDto): boolean {
    return entry.auth.state !== 'authorised' && entry.auth.state !== 'unknown';
  }

  protected requestLabel(entry: SparkCreatorVideoDto): string {
    if (this.requestingVideoId() === entry.videoId) {
      return entry.auth.state === 'requested' ? 'Checking…' : 'Requesting…';
    }
    return entry.auth.state === 'requested' ? 'Check again' : 'Request permission';
  }

  protected requestPostAuth(entry: SparkCreatorVideoDto): void {
    const videoId = entry.videoId;
    if (!videoId || this.requestingVideoId()) {
      return;
    }

    this.requestingVideoId.set(videoId);
    const request =
      entry.auth.state === 'requested'
        ? this.authService.getStatus(videoId, { briefId: this.briefId() })
        : this.authService.requestAuth({ videoId, briefId: this.briefId() });

    request
      .pipe(
        catchError((error: unknown) => {
          this.toast.alert({
            title: 'Could not update Spark permission',
            message: this.messageFor(error),
            type: 'error',
            icon: 'error',
          });
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        this.requestingVideoId.set(undefined);
        if (!status) {
          return;
        }
        this.library.update((videos) =>
          videos.map((item) => (item.videoId === videoId ? { ...item, auth: status } : item)),
        );
      });
  }

  protected setPostSort(sort: SparkPostSort): void {
    this.postSort.set(sort);
  }

  protected showPastedCode(): void {
    this.usePastedCode.set(true);
  }

  protected showLibrary(): void {
    this.usePastedCode.set(false);
  }

  protected setPastedCode(value: string): void {
    this.pastedCode.set(value);
  }

  protected selectCallToAction(value: string): void {
    this.wizard.call_to_action.set(value);
    this.wizard.call_to_action_active.set(true);
  }

  protected setLandingPageUrl(value: string): void {
    this.wizard.landing_page_url.set(value);
  }

  protected ctaPillClass(value: string): string {
    return this.wizard.call_to_action() === value
      ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-gray-900'
      : 'border-border bg-card text-secondary hover:border-gray-400';
  }

  protected openGlossary(): void {
    this.glossaryService.open();
  }

  // --- Step 4: launch -------------------------------------------------------

  protected readonly summaryBudget = computed(() => {
    const currency = this.manageBriefTabsService.focussedBrief()?.currentPrice?.currencyCode ?? null;
    const amount = formatCurrency(this.wizard.budgetAdGroup(), currency, 0);
    return `${amount} ${this.budgetIsDaily() ? 'per day' : 'lifetime'}`;
  });

  /**
   * Creates the campaign (unless one already exists for this brief), then the ad group,
   * then the Spark ad. Sequential because each level needs the id from the one above.
   */
  protected launch(): void {
    if (this.isLaunching() || this.hasLaunched()) {
      return;
    }

    this.launchError.set(null);
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief?.shoutBrief?.id) {
      this.launchError.set('This brief is missing its Shout brief, so no campaign can be created.');
      this.launchStage.set('error');
      return;
    }

    const advertiserId = this.wizard.selectedAdvertiserAccountId();
    const objective = this.wizard.selectedAdvertisingObjective()?.api_value;
    if (!advertiserId || !objective) {
      this.launchError.set('Choose an ad account and a goal before launching.');
      this.launchStage.set('error');
      return;
    }

    const existing = this.existingCampaign();
    this.launchStage.set('campaign');

    const campaign$ = existing
      ? of(existing)
      : this.sparkAdsService.createCampaign({
          briefId: brief.id,
          shoutBriefId: brief.shoutBrief.id,
          ttAdvertiserId: advertiserId,
          objectiveType: objective,
          campaignName: this.wizard.campaignName() ?? brief.title ?? 'Spark campaign',
          budget: this.wizard.budget(),
          budgetMode: this.wizard.selectedBudgetMode(),
          budgetOptimizeOn: this.wizard.budgetOptimizeOn(),
        } satisfies CreateSparkCampaignDto);

    campaign$
      .pipe(
        switchMap((campaign) => {
          this.existingCampaign.set(campaign);
          this.wizard.currentSparkCampaign.set(campaign);
          this.launchStage.set('adGroup');

          return this.sparkAdsService.createSparkAdGroup({
            advertiser_id: advertiserId,
            campaign_id: campaign.tiktokCampaignId!,
            adgroup_name: this.wizard.adGroupName() ?? 'Spark ad group',
            placement_type: this.wizard.placement_type(),
            placements: [...this.wizard.placements()],
            location_ids: [...this.wizard.location_ids()],
            age: [...this.wizard.age()],
            gender: this.wizard.gender(),
            languages: this.wizard.languages().length ? [...this.wizard.languages()] : undefined,
            operating_systems: [...this.wizard.operating_systems()],
            budget_mode: this.wizard.selectedBudgetModeAdGroup(),
            budget: this.wizard.budgetAdGroup(),
            schedule_type: 'SCHEDULE_START_END',
            schedule_start_time: toTikTokTime(this.wizard.startDateAdGroup()),
            schedule_end_time: toTikTokTime(this.wizard.endDateAdGroup()),
            optimization_goal: this.wizard.optimization_goal(),
            billing_event: this.wizard.billing_event(),
            bid_type: this.wizard.bid_type(),
            bid_price:
              this.wizard.bid_type() === 'BID_TYPE_CUSTOM' ? this.wizard.bid_price() : undefined,
            optimization_event: this.wizard.optimization_event(),
            pixel_id: this.wizard.pixel_id(),
            /**
             * TikTok requires a frequency cap when optimising for REACH and rejects
             * it for every other goal, so these are sent only in that case.
             * `undefined` keys are dropped by JSON serialisation.
             */
            frequency: this.needsFrequencyCap() ? (this.wizard.frequency() ?? 3) : undefined,
            frequency_schedule: this.needsFrequencyCap()
              ? (this.wizard.frequency_schedule() ?? 1)
              : undefined,
            pacing: 'PACING_MODE_SMOOTH',
            operation_status: 'ENABLE',
          });
        }),
        switchMap((adGroup) => {
          this.wizard.currentSparkAdGroup.set(adGroup);
          this.launchStage.set('ad');

          const videoId = this.wizard.video_id();
          const landingPage = this.wizard.landing_page_url();

          return this.sparkAdsService.createSparkAd({
            ad_values: {
              ad_name: `${this.wizard.adGroupName() ?? 'Spark ad'} · ${
                this.selectedVideo()?.handle ?? 'creator'
              }`,
              ad_format: this.wizard.ad_format(),
              // AUTH_CODE: the ad runs from the creator's own handle.
              identity_type: 'AUTH_CODE',
              call_to_action: this.wizard.call_to_action(),
              landing_page_url: landingPage ? normaliseUrl(landingPage) : undefined,
            },
            shout_office_use: {
              video_id: videoId!,
              advertiser_id: advertiserId,
              adgroup_id: adGroup.tiktokAdGroupId!,
            },
          });
        }),
        catchError((error: unknown) => {
          this.launchError.set(this.messageFor(error));
          this.launchStage.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }
        this.launchedAdId.set(response.sparkAd?.tiktokAdId ?? response.data?.ad_ids?.[0]);
        this.launchStage.set('done');
        this.toast.alert({
          title: 'Spark ad submitted',
          message: 'TikTok is reviewing it now — usually under an hour.',
          type: 'success',
          icon: 'success',
        });
      });
  }

  protected goToDashboard(): void {
    this.store.setView('dashboard');
  }

  protected retryLaunch(): void {
    this.launchStage.set('idle');
    this.launchError.set(null);
  }

  // --- Internals ------------------------------------------------------------

  /** Sensible starting point so the first step is not an empty form. */
  private seedDefaults(): void {
    if (!this.wizard.selectedAdvertisingObjective()) {
      const fallback = this.objectives.find(
        (option) => option.apiValue === DEFAULT_SPARK_OBJECTIVE,
      );
      if (fallback) {
        this.wizard.selectedAdvertisingObjective.set({
          objective: fallback.label,
          api_value: fallback.apiValue,
        });
      }
    }

    const brief = this.manageBriefTabsService.focussedBrief();
    if (!this.wizard.campaignName() && brief?.title) {
      this.wizard.campaignName.set(brief.title);
    }
  }

  /**
   * Resolves the ad account to spend through and seeds the wizard with it.
   *
   * Without this the advertiser id was only ever set from an already-created campaign,
   * so step 1 blocked on any brief that did not have one yet.
   */
  private loadAdAccount(): void {
    this.reportingService
      .getAdAccount(this.briefId())
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((account) => {
        if (!account) {
          return;
        }
        this.adAccount.set(account);
        if (account.advertiserId && !this.wizard.selectedAdvertiserAccountId()) {
          this.wizard.selectedAdvertiserAccountId.set(account.advertiserId);
        }
        // Targeting options need an advertiser id, so they can only load now.
        this.loadTargetingOptions();
      });
  }

  /**
   * Loads the markets and languages available for this advertiser and objective.
   *
   * The service auto-selects the brand's own country once markets arrive and nothing
   * is chosen, so the common case needs no interaction.
   */
  private loadTargetingOptions(): void {
    const briefId = this.briefId();
    if (!this.wizard.selectedAdvertiserAccountId()) {
      return;
    }
    this.wizard.loadLocations({ briefId, levelRange: 'TO_COUNTRY' });
    this.wizard.loadLanguages({ briefId });
  }

  private loadExistingCampaign(): void {
    this.sparkAdsService
      .getSparkCampaignForBrief(this.briefId())
      .pipe(
        // A brief with no campaign yet is the normal first-run case.
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((campaign) => {
        if (!campaign) {
          return;
        }
        this.existingCampaign.set(campaign);
        this.wizard.currentSparkCampaign.set(campaign);
        if (campaign.advertiserId && !this.wizard.selectedAdvertiserAccountId()) {
          this.wizard.selectedAdvertiserAccountId.set(campaign.advertiserId);
        }
        this.wizard.loadLocations({ briefId: this.briefId(), levelRange: 'TO_COUNTRY' });
        this.wizard.loadLanguages({ briefId: this.briefId() });
      });
  }

  /** Honours a post carried over from the library's "create an ad group for this post". */
  private preselectCarriedVideo(): void {
    const carried = this.store.carriedVideoId();
    if (!carried) {
      return;
    }
    const entry = this.library().find((item) => item.videoId === carried);
    if (entry) {
      this.selectPost(entry);
    }
    this.store.clearCarriedVideo();
  }

  private messageFor(error: unknown): string {
    const candidate = error as { error?: { message?: string }; message?: string };
    return candidate?.error?.message ?? candidate?.message ?? 'Something went wrong.';
  }
}

function postedAt(entry: SparkCreatorVideoDto): number {
  const createTime = entry.video.video_info?.create_time;
  const asNumber = Number(createTime);
  if (Number.isFinite(asNumber)) {
    return asNumber;
  }
  const parsed = new Date(String(createTime).replace(' ', 'T') + 'Z').getTime();
  return Number.isNaN(parsed) ? 0 : parsed / 1000;
}
